/**
 * Sidebar — persistent left navigation.
 *
 * Two sections:
 *   - Primary: 9 ops tabs (Roster, Projection, Booking, Hotel Approval,
 *     Crew View, Dashboard, Reports, Invoices, Analytics)
 *   - Secondary: 9 admin/observability tabs (Settings, Audit Log, Outbox,
 *     Events, Communication, Broadcast, Inventory Alerts, Transport View, RISMA)
 *
 * Visibility per tab is filtered through rolesForRoute() — vendors/crew/
 * finance only see what their role surface permits. The container itself
 * stays mounted; tabs simply omit when not authorised.
 *
 * Prompt 1 / Part 4 — slice pulse. Each NavRow subscribes to its slice key
 * via useSlicePulse(). When a mutation elsewhere in the app emits a pulse
 * for that slice, the row flashes amber for ~1500ms (ring + faint bg + a
 * pulsing dot indicator). This is the visible cascade — clicking Reject
 * lights up Bookings → Audit → Outbox in turn.
 */

import { Link, useLocation } from 'react-router';
import {
  CalendarDays, Cog, FileBarChart, FileText, Flame, Gauge, Hotel as HotelIcon,
  Inbox, LineChart, MapPin, MessageSquare, MessageSquareWarning, Megaphone, ShieldCheck,
  Sparkles, Truck, UserSquare2, History, AlertTriangle, ChevronsLeft,
  ChevronsRight, Building2, Users2, DollarSign, Code2, Plane, Users, LayoutDashboard, type LucideIcon,
} from 'lucide-react';
import { rolesForRoute } from '@/lib/store';
import { useCurrentUser } from '@/lib/hooks/use-current-user';
import { cn } from '@/lib/utils/cn';
import { useSlicePulse, type SliceKey } from '@/lib/visual/pulse';
import { TenantMark } from './TenantMark';

interface NavItem { href: string; label: string; key: string; slice: SliceKey; icon: LucideIcon; }

const PRIMARY: NavItem[] = [
  { key: 'roster',         slice: 'roster',         href: '/roster',         label: 'Roster',         icon: CalendarDays },
  { key: 'projection',     slice: 'projection',     href: '/projection',     label: 'Projection',     icon: Sparkles },
  { key: 'booking',        slice: 'bookings',       href: '/booking',        label: 'Booking',        icon: HotelIcon },
  { key: 'hotel-approval', slice: 'hotelApproval',  href: '/hotel-approval', label: 'Hotel Approval', icon: ShieldCheck },
  { key: 'hotels',         slice: 'hotels',         href: '/hotels',         label: 'Hotels',         icon: Building2 },
  { key: 'stations',       slice: 'hotels',         href: '/stations',       label: 'Stations',       icon: Plane },
  { key: 'crew-master',    slice: 'crew',           href: '/crew-master',    label: 'Crew',           icon: Users },
  // { key: 'crew',           slice: 'crew',           href: '/crew',           label: 'Crew View',      icon: UserSquare2 },
  { key: 'dashboard',      slice: 'dashboard',      href: '/dashboard',      label: 'Dashboard',      icon: Gauge },
  { key: 'console',        slice: 'dashboard',      href: '/console',        label: 'Intelligence Console', icon: LayoutDashboard },
  { key: 'reports',        slice: 'reports',        href: '/reports',        label: 'Reports',        icon: FileBarChart },
  { key: 'accruals',       slice: 'dashboard',      href: '/accruals',       label: 'Accruals',       icon: DollarSign },
  { key: 'invoices',       slice: 'invoices',       href: '/invoices',       label: 'Invoices',       icon: FileText },
  { key: 'analytics',      slice: 'analytics',      href: '/analytics',      label: 'Analytics',      icon: LineChart },
];

const SECONDARY: NavItem[] = [
  { key: 'settings',          slice: 'dashboard',       href: '/settings',          label: 'Settings',          icon: Cog },
  { key: 'audit-log',         slice: 'audit',           href: '/audit-log',         label: 'Audit Log',         icon: History },
  // { key: 'outbox',            slice: 'outbox',          href: '/outbox',            label: 'Outbox',            icon: Inbox },
  { key: 'events',            slice: 'events',          href: '/events',            label: 'Events',            icon: AlertTriangle },
  { key: 'communication',     slice: 'communication',   href: '/communication',     label: 'Communication',     icon: MessageSquare },
  { key: 'broadcast',         slice: 'broadcasts',      href: '/broadcast',         label: 'Broadcast',         icon: Megaphone },
  { key: 'inventory-alerts',  slice: 'inventoryAlerts', href: '/inventory-alerts',  label: 'Inventory Alerts',  icon: AlertTriangle },
  { key: 'peak-dates',        slice: 'dashboard',       href: '/peak-dates',        label: 'Peak Dates',        icon: Flame },
  { key: 'feedback',          slice: 'feedback',        href: '/feedback',          label: 'Feedback',          icon: MessageSquareWarning },
  { key: 'transport-view',    slice: 'bookings',        href: '/transport-view',    label: 'Transport View',    icon: Truck },
  // { key: 'nearest-crew',      slice: 'crew',            href: '/nearest-crew',      label: 'Nearest crew',      icon: Users2 },
  { key: 'api-inventory',     slice: 'dashboard',       href: '/api-inventory',     label: 'API Inventory',     icon: Code2 },
  // { key: 'risma',             slice: 'dashboard',       href: '/risma',             label: 'RISMA',             icon: MapPin },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const { pathname } = useLocation();
  // Decision 6: Real authenticated user takes precedence over mock UserSwitcher
  const activeUser = useCurrentUser();
  const allowed = rolesForRoute(activeUser.role);
  const allowedPrimary = new Set(allowed.primary);
  const allowedSecondary = new Set(allowed.secondary);

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-border bg-card text-card-foreground',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60',
      )}
      aria-label="Primary navigation"
    >
      {/* Brand strip */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-3">
        <TenantMark />
        {!collapsed && (
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-foreground">Rezolv CLASS</div>
          </div>
        )}
      </div>

      {/* Sections */}
      <nav className="flex flex-1 flex-col overflow-y-auto py-3">
        <SectionLabel collapsed={collapsed} label="Operations" />
        <ul className="flex flex-col gap-px px-2">
          {PRIMARY.map((item) => (
            <NavRow
              key={item.key}
              item={item}
              active={pathname === item.href || pathname.startsWith(item.href + '/')}
              disabled={!allowedPrimary.has(item.key)}
              collapsed={collapsed}
            />
          ))}
        </ul>

        <div className="my-3 mx-3 ops-divider" />

        <SectionLabel collapsed={collapsed} label="Admin & Observability" />
        <ul className="flex flex-col gap-px px-2">
          {SECONDARY.map((item) => (
            <NavRow
              key={item.key}
              item={item}
              active={pathname === item.href || pathname.startsWith(item.href + '/')}
              disabled={!allowedSecondary.has(item.key)}
              collapsed={collapsed}
            />
          ))}
        </ul>
      </nav>

      {/* Collapse button — sits at the foot, intentional */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex h-10 shrink-0 items-center gap-2 border-t border-border px-3 text-xs',
          'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
          collapsed && 'justify-center',
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /><span>Collapse</span></>}
      </button>
    </aside>
  );
}

// -----------------------------------------------------------------------------

function SectionLabel({ collapsed, label }: { collapsed: boolean; label: string }) {
  if (collapsed) return null;
  return (
    <div className="px-5 pb-1.5 pt-1 font-mono text-2xs uppercase tracking-[0.16em] text-muted-foreground/70">
      {label}
    </div>
  );
}

interface NavRowProps {
  item: NavItem;
  active: boolean;
  disabled: boolean;
  collapsed: boolean;
}

function NavRow({ item, active, disabled, collapsed }: NavRowProps) {
  const Icon = item.icon;
  const pulsing = useSlicePulse(item.slice);

  const inner = (
    <span
      className={cn(
        'group relative flex h-9 items-center gap-3 rounded-md px-3 text-sm font-medium transition-all duration-200',
        collapsed && 'justify-center px-0',
        active
          ? 'bg-[#072D62] text-white shadow-sm'
          : 'text-muted-foreground hover:bg-[#072D62]/10 hover:text-foreground',
        // Part 4b — slice pulse: amber ring + faint amber bg.
        // Tailwind purges arbitrary classes if not statically present, so we
        // pre-listed the amber utilities here.
        pulsing && 'bg-amber-100/70 ring-1 ring-amber-400/70 text-foreground',
        disabled && 'pointer-events-none opacity-35',
      )}
    >
      <Icon className={cn('h-[18px] w-[18px] shrink-0', active && 'text-white', pulsing && 'text-amber-600')} />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {/* Pulsing dot indicator — Part 4b */}
      {pulsing && (
        <span
          className={cn(
            'absolute right-2 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.7)]',
            'animate-pulse',
            collapsed && 'right-1 top-1',
          )}
          aria-hidden
        />
      )}
    </span>
  );

  if (disabled) {
    return (
      <li>
        <span title="Not available for your role" className="block">{inner}</span>
      </li>
    );
  }

  return (
    <li>
      <Link to={item.href} aria-current={active ? 'page' : undefined} title={collapsed ? item.label : undefined}>
        {inner}
      </Link>
    </li>
  );
}
